import { AxiosError } from 'axios';
import api from './api';
import { getEntityId } from '../utils/entityId';

/** Read-only catalog helpers for sellers (taxonomy is managed by admin). */

export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data as {
      message?: string;
      error?: string;
      errors?: Record<string, string>;
    };
    if (data?.message) return data.message;
    if (data?.error && typeof data.error === 'string') return data.error;
    if (data?.errors) {
      const first = Object.values(data.errors)[0];
      if (first) return first;
    }
    return error.message || fallback;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

export function normalizeCategoryFromApi(raw: Record<string, unknown>) {
  const id = getEntityId(raw as { id?: string; _id?: string });
  const parentRaw = raw.parent;
  let parent: string | null = null;
  if (parentRaw != null && parentRaw !== '') {
    parent =
      typeof parentRaw === 'object'
        ? getEntityId(parentRaw as { id?: string; _id?: string }) || null
        : String(parentRaw);
  }
  const attributes = Array.isArray(raw.attributes)
    ? raw.attributes.map((row: Record<string, unknown>) => {
        const attrRef = row.attribute;
        const attributeId =
          typeof attrRef === 'object' && attrRef !== null
            ? getEntityId(attrRef as { id?: string; _id?: string })
            : String(attrRef ?? '');
        return { ...row, attribute: attributeId };
      })
    : [];
  return { ...raw, id, parent, attributes };
}

export function normalizeBrandFromApi(raw: Record<string, unknown>) {
  return { ...raw, id: getEntityId(raw as { id?: string; _id?: string }) };
}

export async function fetchCategories<T = Record<string, unknown>>(): Promise<T[]> {
  const response = await api.get('/seller/categories');
  const list = (response.data?.data || []) as Record<string, unknown>[];
  return list.map((item) => normalizeCategoryFromApi(item)) as T[];
}

export async function fetchBrands<T = Record<string, unknown>>(): Promise<T[]> {
  const response = await api.get('/seller/brands');
  const list = (response.data?.data || []) as Record<string, unknown>[];
  return list.map((item) => normalizeBrandFromApi(item)) as T[];
}

export async function fetchAttributes<T = Record<string, unknown>>(
  activeOnly = false
): Promise<T[]> {
  const url = activeOnly ? '/seller/attributes/active' : '/seller/attributes';
  const response = await api.get(url);
  const list = (response.data?.data || []) as Record<string, unknown>[];
  return list.map((item) => ({
    ...item,
    id: getEntityId(item as { id?: string; _id?: string }),
    _id: getEntityId(item as { id?: string; _id?: string }),
  })) as T[];
}
